import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * VeraLok padlock + checkmark mark, drawn in SVG so it scales cleanly
 * anywhere in the UI. Uses the official brand palette:
 *   #0077B5 (top gradient) → #005B89 (bottom gradient)
 *   #00B4E6 (primary accent — the checkmark)
 */
export function Logo({ size = 36 }) {
  const gradId = "vl-lock-grad";
  const checkGradId = "vl-check-grad";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      data-testid="brand-logo"
      aria-label="VeraLok"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0077B5" />
          <stop offset="1" stopColor="#005B89" />
        </linearGradient>
        <linearGradient id={checkGradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#33C7EE" />
          <stop offset="1" stopColor="#00B4E6" />
        </linearGradient>
      </defs>
      {/* shackle */}
      <path
        d="M22 26 V19 a10 10 0 0 1 20 0 V26"
        stroke="url(#vl-lock-grad)"
        strokeWidth="4.5"
        strokeLinecap="round"
        fill="none"
      />
      {/* body */}
      <path
        d="M14 26 h36 a4 4 0 0 1 4 4 v22 a6 6 0 0 1 -6 6 h-32 a6 6 0 0 1 -6 -6 v-22 a4 4 0 0 1 4 -4 z"
        fill="url(#vl-lock-grad)"
      />
      {/* checkmark */}
      <path
        d="M22 40 L30 48 L46 32"
        stroke="url(#vl-check-grad)"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* tiny keyhole hint */}
      <circle cx="48" cy="45" r="1.6" fill="#33C7EE" opacity="0.9" />
    </svg>
  );
}

function navBtnClass(active) {
  return "btn-ghost " + (active ? "is-active" : "");
}

export function HeaderBar({ user, onLogout, onNav, view }) {
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    async function poll() {
      try {
        const { data } = await api.get("/notifications");
        if (!cancelled) setUnread(data.unread || 0);
      } catch { /* ignore */ }
    }
    poll();
    const id = setInterval(poll, 6000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, view]);

  return (
    <header className="border-b hairline surface/60 backdrop-blur">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
        <button onClick={() => onNav("console")} className="flex items-center gap-3" data-testid="nav-home-btn">
          <Logo size={30} />
          <div className="leading-tight text-left">
            <div className="font-serif-display text-xl italic" style={{ color: "var(--ink)" }}>
              VeraLok <span className="brand-gradient">Neuralcraft</span>
            </div>
            <div className="label-xs">The 3-Layer &middot; 11-Agent System</div>
          </div>
        </button>
        <nav className="flex items-center gap-2">
          {user ? (
            <>
              <button onClick={() => onNav("console")} className={navBtnClass(view === "console")} data-testid="nav-console-btn">
                Console
              </button>
              <button onClick={() => onNav("history")} className={navBtnClass(view === "history")} data-testid="nav-history-btn">
                Runs
              </button>
              <button
                onClick={() => onNav("inbox")}
                className={"relative " + navBtnClass(view === "inbox")}
                data-testid="nav-inbox-btn"
              >
                Inbox
                {unread > 0 && (
                  <span
                    className="absolute -top-1 -right-1 text-[10px] leading-none font-mono rounded-full w-4 h-4 flex items-center justify-center"
                    style={{ background: "var(--accent)", color: "var(--bg)" }}
                    data-testid="inbox-unread-badge"
                  >
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
              <div className="hidden md:flex flex-col items-end ml-4 mr-2">
                <span className="label-xs">Operator</span>
                <span className="font-mono text-sm">{user.name}</span>
              </div>
              <button onClick={onLogout} className="btn-ghost" data-testid="nav-logout-btn">
                Sign out
              </button>
            </>
          ) : null}
        </nav>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-16 border-t hairline">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Logo size={22} />
          <span className="font-mono text-xs uppercase tracking-[0.22em]" style={{ color: "var(--ink-2)" }}>
            © VeraLok Neuralcraft
          </span>
        </div>
        <div className="font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: "var(--ink-3)" }}>
          The 3-Layer &middot; 11-Agent Intelligence System
        </div>
      </div>
    </footer>
  );
}
